import { Alert, Grid2, IconButton, Typography } from "@mui/material";

import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { Arbor, ARBOR_INITIAL_VALUES } from "@/pages/catalogs/models/Arbor";
import { upsertArbor } from "@/pages/catalogs/repositories/ArborRepository";
import { Stand } from "@/pages/configuration/models/Stand";
import { PartFields } from "@/pages/configuration/pages/parts/PartFields";
import {
	StandGrid,
	useStandsByPart,
} from "@/pages/configuration/pages/parts/StandGrid";
import { setStandsToPart } from "@/pages/configuration/repositories/StandRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Visibility } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

type Props = {
	arbor: Arbor | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const ArborDetails = ({ arbor, modal, onHide }: Props) => {
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const [standsSelected, setStandsSelected] = useState<Stand[]>([]);

	const succesfullyMessage = `The arbor was ${modal}d succesfully`;

	const {
		control,
		reset,
		handleSubmit,
		getValues,
		formState: { dirtyFields },
	} = useForm<Arbor>({
		mode: "onChange",
	});

	const { data: standsOfPart, isSuccess: standsIsSuccess } = useStandsByPart({
		partId: arbor?.PartId!,
		onError: (error) => setError(error.message),
	});
	const standsJson = JSON.stringify(
		standsSelected.map((stand: Stand) => ({
			...stand,
			PartId: arbor?.PartId,
			PartName: arbor?.PartName,
		})),
	);
	const oldStandsJson = JSON.stringify(standsOfPart);

	const isFormDirty =
		!(Object.keys(dirtyFields).length === 0) ||
		standsJson !== oldStandsJson ||
		modal == "create";

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertArbor, {
		onSuccess: async (id: number) => {
			if (id) {
				const resp = await setStandsToPart(id, standsSelected);
				if (!resp.ok) setError(resp.message);
			}

			setNotificationMessage(succesfullyMessage);
			await queryClient.invalidateQueries(["arbors"]);
			await queryClient.invalidateQueries(["standsByPart", id]);
			onHide({ shouldUpdate: true, close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<Arbor> = async (data) => {
		setIsSubmitLoading(true);
		if (modal !== "delete") {
			if (arbor?.IsDuplicate) {
				upsertMutation.mutate({ ...data, PartId: null });
			} else {
				upsertMutation.mutate(data);
			}
		}
	};

	useEffect(() => {
		if (standsOfPart !== undefined) {
			setStandsSelected(standsOfPart || []);
		}
	}, [standsIsSuccess, standsOfPart]);

	useEffect(() => {
		if (modal !== "") {
			reset(arbor || ARBOR_INITIAL_VALUES);
			setIsSubmitLoading(false);
			if (arbor?.IsDuplicate) {
				if (standsOfPart && standsIsSuccess) {
					setStandsSelected(standsOfPart);
				}
			} else {
				setStandsSelected(standsOfPart || []);
			}
		}
	}, [modal, arbor]);

	return (
		<>
			{(modal === "create" || modal === "update") && (
				<HelmetDexteel
					title={`${
						arbor === null ? "New Arbor" : `${arbor?.PartName ?? ""}`
					} - Arbors`}
				/>
			)}
			{arbor === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select an arbor or create a new one</Typography>
				</div>
			) : (
				<form id="upsert-arbor-form" onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={1} p={1}>
						{!!arbor?.IsInActiveCarrier ? (
							<Grid2 size={{ xs: 12 }}>
								<Alert
									severity="warning"
									action={
										<IconButton
											component={Link}
											to={`/catalogs/universal-rolls/${arbor.UniversalRollPartId}`}
											target="_blank"
											size="small"
											title={`See in universal rolls`}
										>
											<Visibility fontSize="small" />
										</IconButton>
									}
								>
									This arbor is in Universal Roll {arbor?.UniversalRollName} in
									Rollset {arbor?.RollsetLetter} (Section:{" "}
									{arbor?.RollsetSectionName}, Stand: {arbor?.RollsetStandName})
									which is assigned to carrier{" "}
									<strong>{arbor?.CarrierName}</strong> (Status:{" "}
									{arbor?.CarrierStatusName}).
								</Alert>
							</Grid2>
						) : null}
						{!!arbor?.IsInActiveRollset && !arbor?.IsInActiveCarrier ? (
							<Grid2 size={{ xs: 12 }}>
								<Alert
									severity="info"
									action={
										<IconButton
											component={Link}
											to={`/catalogs/universal-rolls/${arbor.UniversalRollPartId}`}
											target="_blank"
											size="small"
											title={`See in universal rolls`}
										>
											<Visibility fontSize="small" />
										</IconButton>
									}
								>
									This arbor is in Universal Roll {arbor?.UniversalRollName} in
									an active Rollset {arbor?.RollsetLetter} (Section:{" "}
									{arbor?.RollsetSectionName}, Stand: {arbor?.RollsetStandName}
									).
								</Alert>
							</Grid2>
						) : null}
						<PartFields<Arbor>
							partTypeCode="A"
							control={control}
							disabled={modal === "delete"}
						/>
						<StandGrid
							onSelectionChanged={setStandsSelected}
							disabled={modal === "delete"}
							standsOfPart={standsSelected}
						/>
						<Grid2 size={{ md: 12, sm: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<FooterDetailsButtons
							isSubmitLoading={isSubmitLoading}
							disabled={!isFormDirty}
							onHide={() => onHide({ shouldUpdate: false, close: true })}
						/>
					</Grid2>
				</form>
			)}
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
